import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { presupuestosApi } from '../api/presupuestos'
import api from '../api/client'
import { useAuth } from '../context/AuthContext'
import BuscadorCliente from '../components/BuscadorCliente'
import BuscadorArticulo from '../components/BuscadorArticulo'
import SelectorArticulosModal from '../components/SelectorArticulosModal'
import ModalPegarPedido from '../components/ModalPegarPedido'
import ModalListaClientes from '../components/ModalListaClientes'
import ModalAltaCliente from '../components/ModalAltaCliente'
import ImagenesPresupuesto from '../components/ImagenesPresupuesto'
import Boton from '../components/Boton'
import CeldaStockPresea, { CabeceraStockPresea } from '../components/CeldaStockPresea'
import CotizadorEnvio from '../components/CotizadorEnvio'
import { destinosParaPayload, destinosDesdeApi, repartoDe } from '../utils/envioDestinos'
import useStockPresea from '../hooks/useStockPresea'
import { LABELS_PRESUPUESTO } from '../components/EstadoBadgePresupuesto'
import { etiquetaCliente } from '../utils/cliente'
import { fmtMonto } from '../utils/fecha'
import useDirtyGuard from '../hooks/useDirtyGuard'
import { colors, text, sx, radius } from '../design/tokens'

const inputSx = { padding: '7px 10px', border: `1px solid ${colors.textPlh}`, borderRadius: 6, fontSize: text.body, boxSizing: 'border-box' }
const lblSx = { ...sx.cardTitle, display: 'block', marginBottom: 4 }
const cardSx = { ...sx.card, marginBottom: 14 }

// Redondeo a centavos POR RENGLÓN, igual que PresupuestoItem::getSubtotalAttribute
// en el backend: sin esto el form y el detalle/PDF podían diferir en centavos.
const subtotalItem = (i) =>
  Math.round((parseFloat(i.cantidad) || 0) * (parseFloat(i.precio_unitario) || 0) * (1 - (parseFloat(i.descuento) || 0) / 100) * 100) / 100

// Conversión de precio según el tilde "Precios con IVA": las listas de precios
// vienen CON IVA; destildar divide por (1 + alícuota), tildar multiplica.
const sinIva = (precio, alicuota = 21) => Math.round((precio / (1 + alicuota / 100)) * 100) / 100
const conIva = (precio, alicuota = 21) => Math.round((precio * (1 + alicuota / 100)) * 100) / 100

// ─── Componentes a nivel módulo (identidad estable → los inputs no pierden el foco) ───

function FilaItem({ precioRef, item, mostrarIva, stock, onChange, onQuitar }) {
  return (
    <tr style={{ borderBottom: `1px solid ${colors.borderSoft}` }}>
      <td style={{ padding: '4px 6px' }}>
        {/* Sugerencia #77 (Diego Lloret): la descripción del renglón se edita
            también con artículo del catálogo — personaliza el presupuesto (y la
            NV que salga de él) sin tocar el maestro. El código queda al lado. */}
        {item.articulo_id
          ? <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input value={item.descripcion} onChange={e => onChange('descripcion', e.target.value)}
                title="Descripción de este renglón (editable, no cambia el artículo del catálogo)" style={{ ...inputSx, flex: 1 }} />
              {item.codigo_alfa && <span style={{ ...sx.mono, fontSize: text.meta, color: colors.textMeta, whiteSpace: 'nowrap' }}>{item.codigo_alfa}</span>}
            </span>
          : <>
              <input value={item.descripcion} onChange={e => onChange('descripcion', e.target.value)}
                placeholder="Concepto libre (ej: armado de regalo)" style={{ ...inputSx, width: '100%', fontStyle: 'italic' }} />
              {/* Un renglón libre no tiene artículo: el sistema no sabe si es
                  alimento o bebida y no le calcula la percepción de IVA (Martin,
                  6 oct 2026, PRE-20261006-QR6AF). El tilde lo dice. */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3, fontSize: text.meta, color: colors.textSec, cursor: 'pointer' }}
                title="Marcalo si el renglón es un alimento o una bebida: lleva la percepción de IVA de Presea (la que corresponde a los responsables inscriptos)">
                <input type="checkbox" checked={!!item.percibe_iva} onChange={e => onChange('percibe_iva', e.target.checked)} />
                Alimento o bebida (lleva percepción de IVA)
              </label>
            </>}
      </td>
      <td style={{ padding: '4px 6px' }}>
        <input type="number" min="0" value={item.cantidad} onChange={e => onChange('cantidad', e.target.value)}
          style={{ ...inputSx, width: 64, textAlign: 'right' }} />
      </td>
      {stock && <CeldaStockPresea porCodigo={stock.porCodigo} depositos={stock.depositos} codigo={item.articulo_id ? item.codigo_alfa : null} cantidad={item.cantidad} style={{ padding: '4px 6px' }} />}
      <td style={{ padding: '4px 6px' }}>
        <input type="number" min="0" value={item.precio_unitario} onChange={e => onChange('precio_unitario', e.target.value)}
          style={{ ...inputSx, width: 110, textAlign: 'right' }} />
        {/* Cambiaron la lista o el tipo de precio: lo cargado NO se toca, solo
            se avisa cuánto vale ahora (Martin, 8 oct 2026). */}
        {precioRef != null && Math.abs((parseFloat(item.precio_unitario) || 0) - precioRef) > 0.009 && (
          <div style={{ fontSize: text.meta, color: colors.warn, textAlign: 'right', marginTop: 2, whiteSpace: 'nowrap' }}
            title="El renglón conserva su precio. Si querés el nuevo, escribilo en el campo.">
            ahora {fmtMonto(precioRef)}
          </div>
        )}
      </td>
      <td style={{ padding: '4px 6px' }}>
        <input type="number" min="0" max="100" step="0.0001" value={item.descuento} onChange={e => onChange('descuento', e.target.value)}
          placeholder="0" style={{ ...inputSx, width: 56, textAlign: 'right' }} />
      </td>
      {mostrarIva && (
        <td style={{ padding: '4px 6px' }}>
          <input type="number" min="0" value={item.iva_porcentaje} onChange={e => onChange('iva_porcentaje', e.target.value)}
            style={{ ...inputSx, width: 60, textAlign: 'right' }} />
        </td>
      )}
      <td style={{ padding: '4px 6px', textAlign: 'right', ...sx.mono, whiteSpace: 'nowrap' }}>{fmtMonto(subtotalItem(item))}</td>
      <td style={{ padding: '4px 2px', textAlign: 'right' }}>
        <Boton variante="destructive" sm onClick={onQuitar} title="Quitar ítem">✕</Boton>
      </td>
    </tr>
  )
}

function SeccionItems({ items, mostrarIva, preciosConIva, preciosLista, lista, avisarCambio, stock, onSetItems, onAbrirMulti, onPegarPedido }) {
  const agregar = (articulo) => {
    const precioLista = preciosLista[articulo.id]
    const precio = precioLista == null ? '' : (preciosConIva ? precioLista : sinIva(precioLista))
    onSetItems([...items, {
      articulo_id: articulo.id,
      codigo_alfa: articulo.codigo_alfa,
      descripcion: articulo.descripcion,
      cantidad: 1,
      precio_unitario: precio,
      descuento: '',
      iva_porcentaje: 21,
    }])
  }

  return (
    <div>
      {items.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: text.body }}>
          <thead><tr>
            {['Descripción', 'Cant.', stock && 'Stock', 'Precio unit.', 'Dto %', mostrarIva && 'IVA %', 'Importe', ''].filter(Boolean).map((h, i) => (
              <th key={i} style={{ textAlign: i === 0 || h === 'Stock' ? 'left' : 'right', padding: '4px 6px', fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                {h === 'Stock' ? <CabeceraStockPresea depositos={stock.depositos} /> : h}
              </th>
            ))}
          </tr></thead>
          <tbody>
            {items.map((it, idx) => (
              <FilaItem key={it.uid ?? idx} item={it} mostrarIva={mostrarIva} stock={stock}
                precioRef={avisarCambio && it.articulo_id && preciosLista[it.articulo_id] != null
                  ? (preciosConIva ? preciosLista[it.articulo_id] : sinIva(preciosLista[it.articulo_id])) : null}
                onChange={(campo, valor) => onSetItems(items.map((x, i) => i === idx ? { ...x, [campo]: valor } : x))}
                onQuitar={() => onSetItems(items.filter((_, i) => i !== idx))}
              />
            ))}
          </tbody>
        </table>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
        <div style={{ flex: 1, maxWidth: 360 }}>
          <BuscadorArticulo onSelect={agregar} precios={preciosLista} lista={lista} />
        </div>
        <Boton variante="secondary" sm onClick={onAbrirMulti}>🔍 Buscar artículos</Boton>
        <Boton variante="secondary" sm onClick={onPegarPedido}>📋 Pegar pedido</Boton>
        <Boton variante="ghost" sm onClick={() => onSetItems([...items, { articulo_id: null, descripcion: '', cantidad: 1, precio_unitario: '', descuento: '', iva_porcentaje: 21, percibe_iva: false }])}>+ Concepto libre</Boton>
      </div>
    </div>
  )
}

// ─── Página ────────────────────────────────────────────────────────────────

export default function PresupuestoFormPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { user } = useAuth()
  const editando = Boolean(id)
  // Imágenes de referencia (sug #95): se suben/quitan al instante, no viajan en el guardado.
  const [imagenes, setImagenes] = useState([])

  const [cliente, setCliente] = useState(null)
  const [sinCliente, setSinCliente] = useState(false)
  const [destinatario, setDestinatario] = useState({ nombre: '', email: '', telefono: '', cuit: '' })
  const [vendedores, setVendedores] = useState([])
  const [cab, setCab] = useState({
    // Fecha local (no toISOString: en UTC a la noche ART saltaría al día siguiente)
    vendedor_id: '', fecha: new Date().toLocaleDateString('sv-SE'),
    validez_dias: 15, forma_pago: '', precios_con_iva: true, percepciones_auto: false, imprimir_total: true, observaciones: '',
    // Lista y precio con que se cotiza (Martin, 8 oct 2026). Lista vacía = la
    // del cliente (o LISTA1). tipo_precio: 'venta' (con los descuentos de la
    // lista) o 'lista' (precio de lista lleno, Dto vacío).
    lista_precios: '', tipo_precio: 'venta', imprimir_precio_final: false,
  })
  const [items, setItems] = useState([])
  const [grupos, setGrupos] = useState([])   // [{uid, titulo, tipo, conjunto, items[]}] — uid estable para keys
  // Stock en vivo de Presea (sug #80): el mismo hook y la misma celda que la NV.
  const stock = useStockPresea()
  const codigosStock = [...items, ...grupos.flatMap(g => g.items)].filter(i => i.articulo_id).map(i => i.codigo_alfa).filter(Boolean)
  const [extras, setExtras] = useState([])
  // Envío múltiple (T80): destinos de entrega con sus artículos — docs/diseno-envio-multiple.md
  const [envioDestinos, setEnvioDestinos] = useState([])
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState(null)

  // Presupuesto a medio armar no se pierde con F5 / click al sidebar.
  useDirtyGuard(!guardando && (items.length > 0 || grupos.length > 0 || extras.length > 0 || !!cliente))

  const [showListaClientes, setShowListaClientes] = useState(false)
  const [showAltaCliente, setShowAltaCliente] = useState(false)
  const [multiTarget, setMultiTarget] = useState(null) // 'sueltos' | uid de grupo
  const [pegarPedidoTarget, setPegarPedidoTarget] = useState(null) // 'sueltos' | uid de grupo
  const [iaResultado, setIaResultado] = useState(null) // {preseleccion, avisos} — precarga el selector único al volver de "Pegar pedido"

  // uid incremental para keys estables (el nombre del conjunto NO es key: se puede renombrar sin perder foco)
  const uidRef = useRef(1)
  const nuevoUid = () => uidRef.current++

  // Lista de precios: la elegida; si no se eligió, la del cliente; si no tiene, LISTA1 (default del negocio)
  const listaEfectiva = cab.lista_precios || cliente?.lista_precios || 'LISTA1'
  const [listas, setListas] = useState([])
  useEffect(() => {
    api.get('/listas-precios').then(({ data }) => setListas(Array.isArray(data) ? data : (data.data || []))).catch(() => {})
  }, [])
  const [mapaListas, setMapaListas] = useState({ venta: {}, lista: {} })
  useEffect(() => {
    api.get(`/listas-precios/codigo/${encodeURIComponent(listaEfectiva)}/items`).then(({ data }) => {
      const venta = {}
      const lista = {}
      const filas = Array.isArray(data) ? data : (data.data || [])
      filas.forEach(it => {
        venta[it.articulo_id] = parseFloat(it.precio)
        // Sin precio de lista cargado no se inventa uno: el artículo queda sin
        // precio sugerido en modo "lista".
        if (it.pr_lista != null) lista[it.articulo_id] = parseFloat(it.pr_lista)
      })
      setMapaListas({ venta, lista })
    }).catch(() => setMapaListas({ venta: {}, lista: {} }))
  }, [listaEfectiva])
  const preciosLista = cab.tipo_precio === 'lista' ? mapaListas.lista : mapaListas.venta
  // Se cambió la lista o el tipo de precio en esta edición: avisar en los
  // renglones cuyo precio ya no coincide (sin tocarlos).
  const [cambioPrecios, setCambioPrecios] = useState(false)
  const cambiarPrecios = (campo, valor) => {
    setCab(c => ({ ...c, [campo]: valor }))
    setCambioPrecios(true)
  }

  useEffect(() => {
    api.get('/usuarios/vendedores').then(({ data }) => setVendedores(Array.isArray(data) ? data : (data.data || []))).catch(() => {})
  }, [])

  useEffect(() => {
    if (!editando && user?.id) setCab(c => (c.vendedor_id ? c : { ...c, vendedor_id: user.id }))
  }, [editando, user])

  // F6 abre el buscador avanzado de clientes (mismo atajo que NV)
  useEffect(() => {
    const handler = (e) => { if (e.key === 'F6') { e.preventDefault(); setShowListaClientes(true) } }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    if (!editando) return
    presupuestosApi.obtener(id).then(({ data: p }) => {
      if (p.estado !== 'borrador') { navigate(`/presupuestos/${id}`, { replace: true }); return }
      setCliente(p.cliente)
      setSinCliente(!p.cliente && !!p.destinatario)
      if (p.destinatario) setDestinatario({ nombre: '', email: '', telefono: '', cuit: '', ...p.destinatario })
      setCab({
        vendedor_id: p.vendedor_id || '', fecha: p.fecha?.slice(0, 10) || '',
        validez_dias: p.validez_dias ?? '', forma_pago: p.forma_pago || '',
        precios_con_iva: p.precios_con_iva, percepciones_auto: p.percepciones_auto ?? false, imprimir_total: p.imprimir_total ?? true, observaciones: p.observaciones || '',
        lista_precios: p.lista_precios || '', tipo_precio: p.tipo_precio || 'venta', imprimir_precio_final: !!p.imprimir_precio_final,
      })
      const mapItem = i => ({
        uid: nuevoUid(), articulo_id: i.articulo_id, codigo_alfa: i.articulo?.codigo_alfa,
        descripcion: i.descripcion, cantidad: parseFloat(i.cantidad),
        precio_unitario: parseFloat(i.precio_unitario), descuento: parseFloat(i.descuento) || '',
        iva_porcentaje: parseFloat(i.iva_porcentaje), percibe_iva: !!i.percibe_iva,
      })
      setItems((p.items || []).filter(i => !i.grupo_id).map(mapItem))
      setGrupos((p.grupos || []).map(g => ({ uid: nuevoUid(), titulo: g.titulo, tipo: g.tipo, conjunto: g.conjunto, items: (g.items || []).map(mapItem) })))
      setExtras(p.extras || [])
      setEnvioDestinos(destinosDesdeApi(p.envio_destinos))
      setImagenes(p.imagenes || [])
    })
  }, [editando, id, navigate])

  // ── Tilde IVA: al cambiar, los precios cargados se convierten (sacar/incluir el impuesto) ──
  const toggleIva = (nuevoConIva) => {
    if (nuevoConIva === cab.precios_con_iva) return
    const conv = (i) => {
      const precio = parseFloat(i.precio_unitario)
      if (!precio) return i
      const ali = parseFloat(i.iva_porcentaje) || 21
      return { ...i, precio_unitario: nuevoConIva ? conIva(precio, ali) : sinIva(precio, ali) }
    }
    setItems(its => its.map(conv))
    setGrupos(gs => gs.map(g => ({ ...g, items: g.items.map(conv) })))
    setCab(c => ({ ...c, precios_con_iva: nuevoConIva }))
  }

  // ── "Este cliente ya tiene un presupuesto en curso" (Martin, 8 sep 2026) ──
  // Aviso, no bloqueo: lo ideal es gestionar la venta bajo un solo número, pero
  // dos números para el mismo cliente siguen siendo posibles.
  const [enCurso, setEnCurso] = useState([])
  useEffect(() => {
    if (editando || !cliente?.id) {
      const t = setTimeout(() => setEnCurso([]), 0)
      return () => clearTimeout(t)
    }
    let vivo = true
    presupuestosApi.enCurso(cliente.id).then(({ data }) => { if (vivo) setEnCurso(data || []) }).catch(() => {})
    return () => { vivo = false }
  }, [editando, cliente?.id])

  // ── Percepciones automáticas EN VIVO ──────────────────────────────────
  // La regla vive en el backend (POST /presupuestos/percepciones-preview, el
  // MISMO cálculo del guardado). Sin esto el total del form no incluía la
  // percep de IVA/IIBB y decía menos que el PDF (PRE-20260907-OP2QP, 7 sep 2026).
  const [percepAuto, setPercepAuto] = useState(null)
  // Si el cálculo falla, el total del form quedaría SIN percepciones y diría
  // menos que el PDF sin que nadie lo note: se avisa en vez de callar.
  const [percepError, setPercepError] = useState(false)
  useEffect(() => {
    if (!cab.percepciones_auto || !cliente?.id) {
      const t = setTimeout(() => { setPercepAuto(null); setPercepError(false) }, 0)
      return () => clearTimeout(t)
    }
    const linea = (i) => ({
      articulo_id: i.articulo_id || null,
      cantidad: parseFloat(i.cantidad) || 0,
      precio_unitario: parseFloat(i.precio_unitario) || 0,
      descuento: parseFloat(i.descuento) || 0,
      iva_porcentaje: parseFloat(i.iva_porcentaje) || 0,
      percibe_iva: !i.articulo_id && !!i.percibe_iva,
    })
    const base = [...items, ...grupos.filter(g => g.tipo === 'suma').flatMap(g => g.items)].map(linea)
    const escenarios = { general: base }
    grupos.filter(g => g.tipo === 'opcion').forEach(g => { escenarios[g.uid] = [...base, ...g.items.map(linea)] })
    const t = setTimeout(() => {
      presupuestosApi.percepcionesPreview({ cliente_id: cliente.id, precios_con_iva: cab.precios_con_iva, escenarios })
        .then(({ data }) => { setPercepAuto(data); setPercepError(false) })
        .catch(() => { setPercepAuto(null); setPercepError(true) })
    }, 400)
    return () => clearTimeout(t)
  }, [cab.percepciones_auto, cab.precios_con_iva, cliente?.id, items, grupos])

  // ── Totales en vivo (espejo del backend; con conjuntos NO hay total general) ──
  const totales = useMemo(() => {
    const tot = (its) => {
      const sub = its.reduce((a, i) => a + subtotalItem(i), 0)
      const iva = cab.precios_con_iva ? 0 : its.reduce((a, i) => a + subtotalItem(i) * ((parseFloat(i.iva_porcentaje) || 0) / 100), 0)
      return { sub, iva, total: sub + iva }
    }
    const comun = tot([...items, ...grupos.filter(g => g.tipo === 'suma').flatMap(g => g.items)])
    const aplicarExtras = (base) => extras.reduce((acc, e) => {
      const monto = (e.modo || 'porcentaje') === 'porcentaje' ? base * ((parseFloat(e.valor) || 0) / 100) : (parseFloat(e.valor) || 0)
      return acc + (e.tipo === 'percepcion' ? monto : -monto)
    }, 0)
    const percepDe = (clave) => {
      const pa = percepAuto?.[clave]
      return pa ? (Number(pa.percep_iva) || 0) + (Number(pa.percep_iibb) || 0) : 0
    }
    // Lo que se suma al total se discrimina igual que el IVA (Martin, 6 oct 2026,
    // PRE-20261006-QR6AF): mismo desglose que el detalle y el PDF.
    const desgloseDe = (sub, iva, base, clave) => {
      const d = cab.precios_con_iva ? [['Subtotal (IVA incluido)', sub]] : [['Neto', sub], ['IVA', iva]]
      extras.forEach(e => {
        const monto = (e.modo || 'porcentaje') === 'porcentaje' ? base * ((parseFloat(e.valor) || 0) / 100) : (parseFloat(e.valor) || 0)
        if (monto) d.push([`${e.tipo === 'percepcion' ? 'Percepción' : 'Retención'} ${e.concepto || ''}${(e.modo || 'porcentaje') === 'porcentaje' ? ` (${e.valor}%)` : ''}`, e.tipo === 'percepcion' ? monto : -monto])
      })
      const pa = percepAuto?.[clave]
      if (Number(pa?.percep_iva) > 0) d.push(['Percepción IVA (automática)', Number(pa.percep_iva)])
      if (Number(pa?.percep_iibb) > 0) d.push([`Percepción IIBB CABA ${pa.alicuota_iibb}% (automática)`, Number(pa.percep_iibb)])
      return d
    }
    const conjuntos = {}
    grupos.filter(g => g.tipo === 'opcion').forEach(g => {
      const t = tot(g.items)
      ;(conjuntos[g.conjunto] ||= []).push({ titulo: g.titulo, ...t, escenario: comun.total + t.total + aplicarExtras(comun.total + t.total) + percepDe(g.uid),
        desglose: desgloseDe(comun.sub + t.sub, comun.iva + t.iva, comun.total + t.total, g.uid) })
    })
    return { comun, conjuntos, hayOpciones: Object.keys(conjuntos).length > 0, totalGeneral: comun.total + aplicarExtras(comun.total) + percepDe('general') }
  }, [items, grupos, extras, cab.precios_con_iva, percepAuto])

  // ── Mutadores de grupos (por uid, no por índice ni nombre) ────────────
  const setGrupoUid = (uid, cambios) => setGrupos(gs => gs.map(g => g.uid === uid ? { ...g, ...cambios } : g))
  const renombrarConjunto = (viejo, nuevo) => setGrupos(gs => gs.map(g => g.conjunto === viejo ? { ...g, conjunto: nuevo } : g))

  const agregarPaquete = () => setGrupos([...grupos, { uid: nuevoUid(), titulo: '', tipo: 'suma', items: [] }])
  const agregarConjunto = () => {
    const n = new Set(grupos.filter(g => g.tipo === 'opcion').map(g => g.conjunto)).size + 1
    const nombre = `Conjunto ${n}`
    setGrupos([...grupos,
      { uid: nuevoUid(), titulo: 'Opción 1', tipo: 'opcion', conjunto: nombre, items: [] },
      { uid: nuevoUid(), titulo: 'Opción 2', tipo: 'opcion', conjunto: nombre, items: [] },
    ])
  }

  // El selector único devuelve [{articulo, cantidad}]: la cantidad ya viene elegida.
  const handleMulti = (seleccion) => {
    const nuevos = seleccion.map(({ articulo: a, cantidad }) => {
      const precioLista = preciosLista[a.id]
      return {
        uid: nuevoUid(), articulo_id: a.id, codigo_alfa: a.codigo_alfa, descripcion: a.descripcion,
        cantidad: cantidad || 1, precio_unitario: precioLista == null ? '' : (cab.precios_con_iva ? precioLista : sinIva(precioLista)),
        descuento: '', iva_porcentaje: 21,
      }
    })
    if (multiTarget === 'sueltos') setItems([...items, ...nuevos])
    else {
      const g = grupos.find(x => x.uid === multiTarget)
      if (g) setGrupoUid(multiTarget, { items: [...g.items, ...nuevos] })
    }
    setMultiTarget(null)
    setIaResultado(null)
  }

  // "Pegar pedido" devolvió sus renglones: los que la IA resolvió precargan el
  // selector único (el vendedor los revisa/ajusta antes de confirmar — nunca
  // se agregan solos); los que no pudo matchear quedan como aviso para
  // buscarlos a mano en el mismo modal.
  const alInterpretarPedido = (renglones) => {
    const resueltos = renglones.filter(r => r.articulo)
    const sinResolver = renglones.filter(r => !r.articulo).map(r => r.texto_original)
    setIaResultado({
      preseleccion: resueltos.map(r => ({ articulo: r.articulo, cantidad: r.cantidad })),
      avisos: sinResolver,
    })
    setMultiTarget(pegarPedidoTarget)
    setPegarPedidoTarget(null)
  }

  // Líneas con artículo del catálogo (sueltas + paquetes) para el cotizador de
  // envío, con el precio FINAL (IVA incluido): el umbral de envío gratis es
  // sobre lo que paga el cliente. Las opciones a elegir no entran (no se sabe
  // cuál va a viajar).
  const lineasEnvio = [...items, ...grupos.filter(g => g.tipo === 'suma').flatMap(g => g.items)]
    .filter(i => i.articulo_id && (parseFloat(i.cantidad) || 0) > 0)
    .map(i => {
      const neto = (parseFloat(i.precio_unitario) || 0) * (1 - (parseFloat(i.descuento) || 0) / 100)
      return {
        articulo_id: i.articulo_id, sku: i.codigo_alfa, descripcion: i.descripcion,
        cantidad: parseFloat(i.cantidad) || 0,
        precio: cab.precios_con_iva ? neto : neto * (1 + (parseFloat(i.iva_porcentaje) || 0) / 100),
      }
    })
  // El cotizador devuelve el costo FINAL; en un presupuesto en netos la línea va sin IVA.
  const agregarLineaEnvio = ({ costo, detalle, articulo_envio_id }) => setItems(its => [
    ...its.filter(i => i.codigo_alfa !== 'ENV0000'),
    {
      uid: nuevoUid(), articulo_id: articulo_envio_id, codigo_alfa: 'ENV0000', descripcion: detalle, cantidad: 1,
      precio_unitario: Math.round((cab.precios_con_iva ? costo : costo / 1.21) * 100) / 100, descuento: '', iva_porcentaje: 21,
    },
  ])

  const guardar = async () => {
    setError(null)
    if (envioDestinos.length > 0 && !repartoDe(envioDestinos, lineasEnvio).exacto) {
      setError('El reparto del envío múltiple no cierra: cada artículo tiene que quedar asignado exacto entre los destinos.')
      return
    }
    setGuardando(true)
    try {
      const limpiar = i => ({ ...i, descuento: i.descuento || 0, precio_unitario: i.precio_unitario || 0 })
      const payload = {
        cliente_id: sinCliente ? null : cliente?.id ?? null,
        destinatario: sinCliente ? destinatario : null,
        vendedor_id: cab.vendedor_id || null,
        fecha: cab.fecha,
        validez_dias: cab.validez_dias || null,
        forma_pago: cab.forma_pago || null,
        precios_con_iva: cab.precios_con_iva,
        percepciones_auto: sinCliente ? false : cab.percepciones_auto,
        imprimir_total: cab.imprimir_total,
        // Se guarda la lista con la que se cotizó: la NV que salga se vende con ella.
        lista_precios: listaEfectiva,
        tipo_precio: cab.tipo_precio,
        imprimir_precio_final: cab.imprimir_precio_final,
        observaciones: cab.observaciones || null,
        items: items.map(limpiar),
        grupos: grupos.map(g => ({ titulo: g.titulo, tipo: g.tipo, conjunto: g.conjunto, items: g.items.map(limpiar) })),
        extras,
        envio_destinos: destinosParaPayload(envioDestinos, lineasEnvio),
      }
      const { data } = editando
        ? await presupuestosApi.actualizar(id, payload)
        : await presupuestosApi.crear(payload)
      navigate(`/presupuestos/${data.id}`, { replace: true })
    } catch (e) {
      setError(e.response?.data?.message || Object.values(e.response?.data?.errors || {}).flat().join(' · ') || 'Error al guardar')
    } finally {
      setGuardando(false)
    }
  }

  // ── Bloques en ORDEN DE CREACIÓN (paquetes y conjuntos intercalados):
  //    lo nuevo aparece siempre al final, justo arriba de los botones que lo crean.
  const bloques = useMemo(() => {
    const out = []
    const vistos = new Set()
    grupos.forEach(g => {
      if (g.tipo === 'suma') out.push({ tipo: 'paquete', grupo: g })
      else if (!vistos.has(g.conjunto)) {
        vistos.add(g.conjunto)
        out.push({ tipo: 'conjunto', conjunto: g.conjunto, key: g.uid })
      }
    })
    return out
  }, [grupos])

  const mostrarIva = !cab.precios_con_iva

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>{editando ? 'Editar presupuesto' : 'Nuevo presupuesto'}</h1>
        <Boton variante="ghost" onClick={() => navigate(-1)}>← Volver</Boton>
      </div>

      {/* Cliente — misma UX que la NV */}
      <div style={cardSx}>
        {!sinCliente && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              {cliente
                ? (
                  <div style={{ background: colors.linkBg, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '8px 12px', fontSize: text.body }}>
                    <strong>{etiquetaCliente(cliente)}</strong>
                    {cliente.cuit && <span style={{ marginLeft: 10, ...sx.mono, fontSize: text.sec }}>CUIT {cliente.cuit}</span>}
                    {cliente.lista_precios && <span style={{ marginLeft: 10, fontSize: text.sec, color: colors.link, fontWeight: 600 }}>Lista: {cliente.lista_precios}</span>}
                    <Boton variante="ghost" sm style={{ marginLeft: 10 }} onClick={() => setCliente(null)}>cambiar</Boton>
                  </div>
                )
                : <BuscadorCliente value={cliente} onChange={setCliente} />}
              {enCurso.length > 0 && (
                <div style={{ marginTop: 8, background: colors.warnBg, border: `1px solid ${colors.warnBorder}`, borderRadius: radius.md, padding: '8px 12px', fontSize: text.sec, color: colors.warn, fontWeight: 500 }}>
                  Este cliente ya tiene {enCurso.length === 1 ? 'un presupuesto' : `${enCurso.length} presupuestos`} en curso:{' '}
                  {enCurso.map((e, i) => (
                    <span key={e.id}>
                      {i > 0 && ' · '}
                      <Link to={`/presupuestos/${e.id}`} style={{ ...sx.mono, color: colors.warn, fontWeight: 600 }}>{e.numero} v{e.version}</Link> ({(LABELS_PRESUPUESTO[e.estado] || e.estado).toLowerCase()})
                    </span>
                  ))}
                  . Lo ideal es seguir la venta bajo ese número con una versión nueva; este presupuesto aparte también vale.
                </div>
              )}
            </div>
            {!cliente && <>
              <Boton variante="secondary" onClick={() => setShowListaClientes(true)} title="Buscar cliente (F6)">🔍</Boton>
              <Boton variante="secondary" onClick={() => setShowAltaCliente(true)}>+ Nuevo cliente</Boton>
            </>}
          </div>
        )}
        {!sinCliente && !cliente && <div style={{ fontSize: text.meta, color: colors.textMeta, marginTop: 4 }}>F6 abre la búsqueda avanzada (nombre, CUIT, código, localidad).</div>}

        {sinCliente && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input value={destinatario.nombre} onChange={e => setDestinatario({ ...destinatario, nombre: e.target.value })}
              placeholder="Nombre / razón social *" style={{ ...inputSx, flex: 1, minWidth: 240 }} />
            <input value={destinatario.email} onChange={e => setDestinatario({ ...destinatario, email: e.target.value })} placeholder="Email" style={{ ...inputSx, width: 210 }} />
            <input value={destinatario.telefono} onChange={e => setDestinatario({ ...destinatario, telefono: e.target.value })} placeholder="Teléfono" style={{ ...inputSx, width: 140 }} />
            <input value={destinatario.cuit} onChange={e => setDestinatario({ ...destinatario, cuit: e.target.value })} placeholder="CUIT" style={{ ...inputSx, width: 140 }} />
          </div>
        )}
        <label style={{ fontSize: text.sec, color: colors.textSec, display: 'inline-flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
          <input type="checkbox" checked={sinCliente} onChange={e => { setSinCliente(e.target.checked); setCliente(null) }} />
          No es cliente del maestro (datos libres)
        </label>

        <div style={{ display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={lblSx}>Vendedor</label>
            <select value={cab.vendedor_id} onChange={e => setCab({ ...cab, vendedor_id: e.target.value })} style={{ ...inputSx, width: 170 }}>
              {vendedores.map(v => <option key={v.id} value={v.id}>{v.nombre}</option>)}
            </select>
          </div>
          <div>
            <label style={lblSx}>Fecha</label>
            <input type="date" value={cab.fecha} onChange={e => setCab({ ...cab, fecha: e.target.value })} style={inputSx} />
          </div>
          <div>
            <label style={lblSx}>Validez (días)</label>
            <input type="number" value={cab.validez_dias} onChange={e => setCab({ ...cab, validez_dias: e.target.value })} style={{ ...inputSx, width: 80 }} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={lblSx}>Forma de pago</label>
            <input value={cab.forma_pago} onChange={e => setCab({ ...cab, forma_pago: e.target.value })}
              placeholder="Ej: 50% anticipo, 50% contra entrega" style={{ ...inputSx, width: '100%' }} />
          </div>
          <div>
            <label style={lblSx}>Lista de precios</label>
            <select value={listaEfectiva} onChange={e => cambiarPrecios('lista_precios', e.target.value)} style={{ ...inputSx, width: 140 }}>
              {!listas.some(l => l.codigo === listaEfectiva) && <option value={listaEfectiva}>{listaEfectiva}</option>}
              {listas.map(l => <option key={l.id} value={l.codigo}>{l.codigo}</option>)}
            </select>
          </div>
          <div>
            <label style={lblSx}>Precio</label>
            <select value={cab.tipo_precio} onChange={e => cambiarPrecios('tipo_precio', e.target.value)} style={{ ...inputSx, width: 150 }}
              title="Venta: el precio de la lista con sus descuentos. Lista: el precio de lista lleno, sin descuento (lo cargás a mano en Dto %).">
              <option value="venta">De venta</option>
              <option value="lista">De lista (sin dto)</option>
            </select>
          </div>
          <label style={{ fontSize: text.sec, color: colors.textSec, display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 8, whiteSpace: 'nowrap' }}
            title="Al cambiarlo, los precios ya cargados se convierten (se les saca o incluye el IVA)">
            <input type="checkbox" checked={cab.precios_con_iva} onChange={e => toggleIva(e.target.checked)} />
            Precios con IVA
          </label>
          {/* Sug #39: percepciones calculadas solas según el perfil impositivo
              del cliente (percep IVA por artículo + IIBB del padrón AGIP).
              Solo con cliente del maestro — un destinatario libre no tiene
              perfil. Se suman al guardar; se ven en el detalle y el PDF. */}
          {!sinCliente && (
            <label style={{ fontSize: text.sec, color: colors.textSec, display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 8, whiteSpace: 'nowrap' }}
              title="Calcula percepción de IVA (por artículo) e IIBB CABA (padrón AGIP / ficha) según el perfil del cliente — solo responsables inscriptos. Se suman al total al guardar.">
              <input type="checkbox" checked={cab.percepciones_auto} onChange={e => setCab(c => ({ ...c, percepciones_auto: e.target.checked }))} />
              Percepciones automáticas
            </label>
          )}
          <label style={{ fontSize: text.sec, color: colors.textSec, display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 8, whiteSpace: 'nowrap' }}
            title="Destildado, el PDF muestra el detalle sin el TOTAL GENERAL (los totales por opción se imprimen igual)">
            <input type="checkbox" checked={cab.imprimir_total} onChange={e => setCab(c => ({ ...c, imprimir_total: e.target.checked }))} />
            Imprimir total general
          </label>
          <label style={{ fontSize: text.sec, color: colors.textSec, display: 'flex', gap: 6, alignItems: 'center', paddingBottom: 8, whiteSpace: 'nowrap' }}
            title="Tildado, el PDF, el TXT y el Excel muestran el precio unitario ya descontado y sin la columna Dto. Destildado, el precio de cada renglón con su Dto al lado.">
            <input type="checkbox" checked={cab.imprimir_precio_final} onChange={e => setCab(c => ({ ...c, imprimir_precio_final: e.target.checked }))} />
            Imprimir precio final (sin columna Dto)
          </label>
        </div>
        <div style={{ fontSize: text.meta, color: colors.textMeta, marginTop: 6 }}>
          Precios sugeridos de la lista <b>{listaEfectiva}</b>
          {cab.lista_precios ? '' : (cliente?.lista_precios ? ' (lista del cliente)' : ' (default)')}
          {cab.tipo_precio === 'lista' ? ' — precio de LISTA, sin descuento' : ' — precio de venta'}
          {mostrarIva ? ' — convertidos a SIN IVA' : ''} — siempre editables. La nota de venta que salga se vende con esta lista.
        </div>
        {cambioPrecios && (
          <div style={{ fontSize: text.sec, color: colors.warn, marginTop: 6 }}>
            Cambiaste la lista o el tipo de precio: los renglones ya cargados conservan su precio. Donde el nuevo es distinto, abajo del precio dice “ahora $…”; lo que agregues desde acá se cotiza con la elección nueva.
          </div>
        )}
      </div>

      {/* Ítems generales */}
      <div style={cardSx}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: text.title }}>Ítems generales</h3>
          {/* Sug #80: mismo stock en vivo que la NV, de TODOS los renglones
              (sueltos, paquetes y opciones) — la columna aparece en cada tabla. */}
          {codigosStock.length > 0 && (
            <Boton variante="secondary" sm style={{ marginLeft: 'auto' }} onClick={() => stock.consultar(codigosStock)} disabled={stock.cargando}
              title="Consulta el stock EN VIVO en Presea por depósito, de los artículos cargados en todo el presupuesto">
              {stock.cargando ? '⏳ Consultando…' : '📦 Consultar stock'}
            </Boton>
          )}
        </div>
        {stock.error && (
          <div style={{ marginBottom: 10, fontSize: text.sec, color: colors.err, background: colors.errBg, border: `1px solid ${colors.errBorder}`, borderRadius: 8, padding: '6px 10px' }}>
            {stock.error}
          </div>
        )}
        <SeccionItems items={items} mostrarIva={mostrarIva} preciosConIva={cab.precios_con_iva} preciosLista={preciosLista} lista={listaEfectiva} avisarCambio={cambioPrecios} stock={stock.porCodigo ? stock : null}
          onSetItems={setItems} onAbrirMulti={() => setMultiTarget('sueltos')} onPegarPedido={() => setPegarPedidoTarget('sueltos')} />
      </div>

      {/* Paquetes y conjuntos, intercalados en orden de creación */}
      {bloques.map(b => b.tipo === 'paquete' ? (
        <div key={b.grupo.uid} style={{ ...cardSx, borderLeft: `3px solid ${colors.border}` }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
            <input value={b.grupo.titulo} onChange={e => setGrupoUid(b.grupo.uid, { titulo: e.target.value })}
              placeholder="Título del paquete" style={{ ...inputSx, fontWeight: 600, width: 320 }} />
            <span style={{ fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Paquete · suma al total</span>
            <span style={{ marginLeft: 'auto', ...sx.mono, fontWeight: 600 }}>{fmtMonto(b.grupo.items.reduce((a, i) => a + subtotalItem(i), 0))}</span>
            <Boton variante="destructive" sm onClick={() => setGrupos(gs => gs.filter(g => g.uid !== b.grupo.uid))}>✕ Quitar</Boton>
          </div>
          <SeccionItems items={b.grupo.items} mostrarIva={mostrarIva} preciosConIva={cab.precios_con_iva} preciosLista={preciosLista} lista={listaEfectiva} avisarCambio={cambioPrecios} stock={stock.porCodigo ? stock : null}
            onSetItems={its => setGrupoUid(b.grupo.uid, { items: its })} onAbrirMulti={() => setMultiTarget(b.grupo.uid)} onPegarPedido={() => setPegarPedidoTarget(b.grupo.uid)} />
        </div>
      ) : (
        <div key={b.key} style={{ ...cardSx, borderLeft: `3px solid ${colors.text}` }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: text.meta, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, color: colors.textSec }}>A elegir:</span>
            <input value={b.conjunto} onChange={e => renombrarConjunto(b.conjunto, e.target.value)} style={{ ...inputSx, fontWeight: 600, width: 280 }} />
            <span style={{ fontSize: text.sec, color: colors.textSec }}>cada opción tiene su total — el cliente elige una</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <Boton variante="secondary" sm onClick={() => setGrupos(gs => [...gs, { uid: nuevoUid(), titulo: `Opción ${gs.filter(g => g.conjunto === b.conjunto).length + 1}`, tipo: 'opcion', conjunto: b.conjunto, items: [] }])}>+ Opción</Boton>
              <Boton variante="destructive" sm onClick={() => setGrupos(gs => gs.filter(g => g.conjunto !== b.conjunto))}>✕ Quitar</Boton>
            </div>
          </div>
          {grupos.filter(g => g.tipo === 'opcion' && g.conjunto === b.conjunto).map(g => (
            <div key={g.uid} style={{ border: `1px solid ${colors.borderSoft}`, borderRadius: 6, padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <input value={g.titulo} onChange={e => setGrupoUid(g.uid, { titulo: e.target.value })}
                  placeholder="Nombre de la opción" style={{ ...inputSx, width: 280 }} />
                <span style={{ marginLeft: 'auto', ...sx.mono, fontWeight: 700 }}>{fmtMonto(g.items.reduce((a, i) => a + subtotalItem(i), 0))}</span>
                <Boton variante="destructive" sm onClick={() => setGrupos(gs => gs.filter(x => x.uid !== g.uid))}>✕</Boton>
              </div>
              <SeccionItems items={g.items} mostrarIva={mostrarIva} preciosConIva={cab.precios_con_iva} preciosLista={preciosLista} lista={listaEfectiva} avisarCambio={cambioPrecios} stock={stock.porCodigo ? stock : null}
                onSetItems={its => setGrupoUid(g.uid, { items: its })} onAbrirMulti={() => setMultiTarget(g.uid)} onPegarPedido={() => setPegarPedidoTarget(g.uid)} />
            </div>
          ))}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <Boton variante="secondary" onClick={agregarPaquete}>+ Paquete</Boton>
        <Boton variante="secondary" onClick={agregarConjunto}>+ Conjunto de opciones a elegir</Boton>
      </div>

      {/* Envío: un destino (a la dirección del cliente) o varios (T80) — el mismo cotizador que la NV */}
      <div style={cardSx}>
        <CotizadorEnvio
          lineas={lineasEnvio}
          direccion={cliente ? { provincia: cliente.provincia, localidad: cliente.localidad, codigo_postal: cliente.codigo_postal } : null}
          destinos={envioDestinos}
          onDestinosChange={setEnvioDestinos}
          onAgregarLinea={agregarLineaEnvio}
          inputStyle={inputSx}
        />
      </div>

      {/* Percepciones / retenciones */}
      <div style={cardSx}>
        <h3 style={{ marginTop: 0, marginBottom: 10, fontSize: text.title }}>Percepciones y retenciones</h3>
        {extras.map((e, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
            <select value={e.tipo} onChange={ev => setExtras(extras.map((x, j) => j === i ? { ...x, tipo: ev.target.value } : x))} style={inputSx}>
              <option value="percepcion">Percepción (+)</option>
              <option value="retencion">Retención (−)</option>
            </select>
            <input value={e.concepto} onChange={ev => setExtras(extras.map((x, j) => j === i ? { ...x, concepto: ev.target.value } : x))}
              placeholder="Concepto (ej: IIBB CABA)" style={{ ...inputSx, width: 240 }} />
            <select value={e.modo || 'porcentaje'} onChange={ev => setExtras(extras.map((x, j) => j === i ? { ...x, modo: ev.target.value } : x))} style={inputSx}>
              <option value="porcentaje">%</option>
              <option value="monto">$ fijo</option>
            </select>
            <input type="number" value={e.valor} onChange={ev => setExtras(extras.map((x, j) => j === i ? { ...x, valor: ev.target.value } : x))}
              style={{ ...inputSx, width: 100, textAlign: 'right' }} />
            <Boton variante="destructive" sm onClick={() => setExtras(extras.filter((_, j) => j !== i))}>✕</Boton>
          </div>
        ))}
        <Boton variante="ghost" sm onClick={() => setExtras([...extras, { tipo: 'percepcion', concepto: '', modo: 'porcentaje', valor: '' }])}>+ Agregar</Boton>
      </div>

      {/* Imágenes para el cliente (sug #95): necesitan el borrador guardado */}
      <div style={cardSx}>
        {editando
          ? <ImagenesPresupuesto presupuestoId={id} imagenes={imagenes} editable onCambio={setImagenes} />
          : <>
              <div style={lblSx}>Imágenes para el cliente</div>
              <p style={{ margin: 0, fontSize: text.sec, color: colors.textSec }}>
                Guardá el borrador y adjuntá las fotos del regalo o del producto desde el presupuesto: salen en el PDF, debajo de los renglones.
              </p>
            </>}
      </div>

      {/* Observaciones + totales + guardar */}
      <div style={cardSx}>
        <label style={lblSx}>Observaciones / condiciones</label>
        <textarea value={cab.observaciones} onChange={e => setCab({ ...cab, observaciones: e.target.value })}
          rows={2} style={{ ...inputSx, width: '100%', resize: 'vertical' }} />

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
          <div style={{ minWidth: 360, fontSize: text.body }}>
            {totales.hayOpciones ? (
              <>
                {/* Con opciones abiertas el total general NO aplica: vale el total de cada opción */}
                {totales.comun.total > 0 && <Linea k="Ítems generales (entran en todas las opciones)" v={totales.comun.total} />}
                {Object.entries(totales.conjuntos).map(([nombre, ops]) => (
                  <div key={nombre} style={{ marginTop: 8 }}>
                    <div style={{ fontSize: text.meta, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, color: colors.textSec }}>{nombre}</div>
                    {ops.map((op, i) => (
                      <div key={i} style={{ padding: '3px 0' }}>
                        {op.desglose.length > 1 && op.desglose.map(([k, v], j) => <Linea key={j} k={k} v={v} />)}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                          <span>Total Opción {i + 1}{op.titulo ? ` — ${op.titulo}` : ''}</span>
                          <span style={sx.mono}>{fmtMonto(op.escenario)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
                <div style={{ fontSize: text.meta, color: colors.textMeta, marginTop: 6 }}>
                  Cada total incluye los ítems generales{extras.length > 0 || percepAuto ? ' y las percepciones/retenciones' : ''}{cab.precios_con_iva ? ' · IVA incluido' : ' · IVA por ítem'}.
                </div>
              </>
            ) : (
              <>
                {mostrarIva && <>
                  <Linea k="Subtotal" v={totales.comun.sub} />
                  <Linea k="IVA" v={totales.comun.iva} />
                </>}
                {Number(percepAuto?.general?.percep_iva) > 0 && (
                  <Linea k="Percepción IVA (automática)" v={percepAuto.general.percep_iva} />
                )}
                {Number(percepAuto?.general?.percep_iibb) > 0 && (
                  <Linea k={`Percepción IIBB CABA ${percepAuto.general.alicuota_iibb}% (automática)`} v={percepAuto.general.percep_iibb} />
                )}
                {percepError && (
                  <div style={{ color: colors.err, fontSize: text.sec, padding: '3px 0' }}>
                    No se pudieron calcular las percepciones automáticas: este total NO las incluye. Reintentá o revisá el PDF.
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `2px solid ${colors.border}`, paddingTop: 6, marginTop: 6, fontWeight: 700, fontSize: text.title }}>
                  <span>TOTAL{cab.precios_con_iva ? ' (IVA inc.)' : ''}</span>
                  <span style={sx.mono}>{fmtMonto(totales.totalGeneral)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {error && <div style={{ color: colors.err, marginTop: 10, fontSize: text.sec }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <Boton variante="ghost" onClick={() => navigate(-1)}>Cancelar</Boton>
          <Boton variante="primary" onClick={guardar} disabled={guardando || (!sinCliente && !cliente) || (sinCliente && !destinatario.nombre)}>
            {guardando ? 'Guardando…' : '💾 Guardar presupuesto'}
          </Boton>
        </div>
      </div>

      {/* Modales */}
      {showListaClientes && (
        <ModalListaClientes
          onSelect={(c) => { setCliente(c); setSinCliente(false); setShowListaClientes(false) }}
          onClose={() => setShowListaClientes(false)}
        />
      )}
      {showAltaCliente && (
        <ModalAltaCliente
          onCreado={(c) => { setCliente(c); setShowAltaCliente(false) }}
          onClose={() => setShowAltaCliente(false)}
        />
      )}
      {pegarPedidoTarget !== null && (
        <ModalPegarPedido
          clienteId={sinCliente ? null : cliente?.id}
          onListo={alInterpretarPedido}
          onClose={() => setPegarPedidoTarget(null)}
        />
      )}
      {multiTarget !== null && (
        <SelectorArticulosModal
          onConfirm={handleMulti}
          onClose={() => { setMultiTarget(null); setIaResultado(null) }}
          precios={preciosLista}
          lista={listaEfectiva}
          preseleccion={iaResultado?.preseleccion}
          avisos={iaResultado?.avisos}
        />
      )}
    </div>
  )
}

function Linea({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: colors.textSec }}>
      <span>{k}</span><span style={sx.mono}>{fmtMonto(v)}</span>
    </div>
  )
}
